/**
 * Phase 1.7 — Vehicles module index, v2.
 *
 * Group-by: service_status (In Service / Maintenance / Out of Service /
 * Retired). KPI strip uses the In Service | Maintenance split per spec.
 *
 * Cards now show the most-recent uploaded image via EntityImage and
 * navigate to /vehicles/:id on click.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Truck, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Card,
  EmptyState,
  FilterBar,
  KpiCard,
  SectionHeader,
  ViewToggle,
  type ViewMode,
} from '@/components/v2';
import { EntityImage } from '@/components/EntityImage';
import { authFetch } from '@/lib/auth';
import { useUiPrefs } from '@/lib/layout-flag';
import { usePageHeader } from '@/components/v2/layout/Header';
import { AddEntityDialog } from '@/components/v2/dialogs/AddEntityDialog';
import type { Vehicle } from '@shared/types';

type Status = NonNullable<Vehicle['service_status']>;
const STATUS_ORDER: Status[] = ['in_service', 'maintenance', 'out_of_service', 'retired'];
const STATUS_LABEL: Record<Status, string> = {
  in_service: 'In Service',
  maintenance: 'Maintenance',
  out_of_service: 'Out of Service',
  retired: 'Retired',
};
const STATUS_TONE: Record<Status, string> = {
  in_service: 'bg-moss/10 text-moss dark:bg-moss-dark/20 dark:text-moss-dark',
  maintenance: 'bg-clay/10 text-clay dark:bg-clay-dark/20 dark:text-clay-dark',
  out_of_service: 'bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark',
  retired: 'bg-token-muted/15 text-token-muted dark:bg-token-muted-dark/20 dark:text-token-muted-dark',
};

const VIEW_KEY = 'vehicles';

export function VehiclesPageV2(): JSX.Element {
  const navigate = useNavigate();
  const { prefs, patch } = useUiPrefs();
  const view: ViewMode = prefs.view_modes?.[VIEW_KEY] ?? 'grid';
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | Status>('all');
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [refreshTick, setRefreshTick] = useState<number>(0);

  usePageHeader(
    'Vehicles',
    <Button
      size="sm"
      onClick={() => setAddOpen(true)}
      className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
    >
      <Plus className="h-4 w-4" />
      Add vehicle
    </Button>,
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    async function load(): Promise<void> {
      try {
        const data = await authFetch<{ items: Vehicle[] }>('/api/vehicles');
        if (!cancelled) setVehicles(data.items ?? []);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load vehicles');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [refreshTick]);

  const filtered = useMemo<Vehicle[]>(() => {
    const q = search.trim().toLowerCase();
    return vehicles.filter((v) => {
      if (q) {
        const haystack = `${v.name} ${v.make ?? ''} ${v.model ?? ''} ${v.license_plate ?? ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (statusFilter !== 'all' && (v.service_status ?? 'in_service') !== statusFilter) return false;
      return true;
    });
  }, [vehicles, search, statusFilter]);

  const grouped = useMemo<Record<Status, Vehicle[]>>(() => {
    const out = STATUS_ORDER.reduce<Record<Status, Vehicle[]>>(
      (acc, s) => ({ ...acc, [s]: [] }),
      {} as Record<Status, Vehicle[]>,
    );
    for (const v of filtered) out[v.service_status ?? 'in_service'].push(v);
    return out;
  }, [filtered]);

  const totals = useMemo(() => {
    const inService = vehicles.filter((v) => (v.service_status ?? 'in_service') === 'in_service').length;
    const maintenance = vehicles.filter((v) => v.service_status === 'maintenance').length;
    return { inService, maintenance };
  }, [vehicles]);

  const setView = (next: ViewMode): void => {
    void patch({ view_modes: { ...(prefs.view_modes ?? {}), [VIEW_KEY]: next } });
  };

  const hasActiveFilters = Boolean(search) || statusFilter !== 'all';
  const clear = (): void => { setSearch(''); setStatusFilter('all'); };

  return (
    <div className="space-y-6">
      <AddEntityDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        title="Add Vehicle"
        endpoint="/api/vehicles"
        entityType="vehicle"
        fields={[
          { name: 'name', label: 'Name', required: true, placeholder: 'e.g. Kubota M5-091' },
          { name: 'make', label: 'Make', half: true, placeholder: 'Kubota' },
          { name: 'model', label: 'Model', half: true, placeholder: 'M5-091' },
          { name: 'year', label: 'Year', type: 'number', half: true, placeholder: '2020' },
          { name: 'license_plate', label: 'License plate', half: true },
          {
            name: 'service_status',
            label: 'Status',
            type: 'select',
            defaultValue: 'in_service',
            options: STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
          },
          { name: 'notes', label: 'Notes', type: 'textarea' },
        ]}
        transform={(v) => ({
          name: v.name,
          make: v.make || null,
          model: v.model || null,
          year: v.year ? Number(v.year) : null,
          license_plate: v.license_plate || null,
          service_status: v.service_status || 'in_service',
          notes: v.notes || null,
        })}
        onAdded={() => setRefreshTick((t) => t + 1)}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          label="Vehicles"
          icon={Truck}
          primary={{ label: 'In Service', value: totals.inService }}
          secondary={{
            label: 'Maintenance',
            value: totals.maintenance,
            tone: totals.maintenance > 0 ? 'warning' : 'default',
          }}
        />
        <KpiCard
          label="Filter"
          primary={{ label: 'Showing', value: filtered.length }}
          secondary={{ label: 'Total', value: vehicles.length }}
        />
        <KpiCard
          label="Service health"
          icon={Wrench}
          primary={{
            label: 'Service ratio',
            value: vehicles.length === 0 ? '–' : `${Math.round((totals.inService / vehicles.length) * 100)}%`,
            tone: 'positive',
          }}
        />
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: 'Search by name, make, plate' }}
        filters={[
          <FilterSelect
            key="status"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as 'all' | Status)}
            options={[
              { value: 'all', label: 'All statuses' },
              ...STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
            ]}
          />,
        ]}
        actions={<ViewToggle value={view} onChange={setView} />}
        onClear={clear}
        hasActiveFilters={hasActiveFilters}
      />

      {error ? (
        <div className="rounded-lg bg-clay/10 p-4 text-sm text-clay dark:bg-clay-dark/20 dark:text-clay-dark">
          {error}
        </div>
      ) : null}

      {loading ? (
        <SkeletonGrid />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Truck}
          title={hasActiveFilters ? 'No matches' : 'No vehicles yet'}
          description={
            hasActiveFilters
              ? 'Try clearing filters to see everything.'
              : 'Add a vehicle to track maintenance, odometer readings, and registrations.'
          }
          action={
            hasActiveFilters ? (
              <Button onClick={clear} variant="outline">Clear filters</Button>
            ) : (
              <Button onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" />
                Add vehicle
              </Button>
            )
          }
        />
      ) : (
        STATUS_ORDER.filter((s) => grouped[s].length > 0).map((status) => (
          <SectionHeader key={status} title={STATUS_LABEL[status]} count={grouped[status].length} collapsible>
            {view === 'grid' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {grouped[status].map((v) => (
                  <VehicleCard key={v.id} vehicle={v} onOpen={() => navigate(`/vehicles/${v.id}`)} />
                ))}
              </div>
            ) : (
              <ListTable
                rows={grouped[status]}
                onRow={(v) => navigate(`/vehicles/${v.id}`)}
                columns={[
                  { header: 'Name', cell: (v) => <span className="font-medium">{v.name}</span> },
                  { header: 'Make / Model', cell: (v) => `${v.make ?? '–'} ${v.model ?? ''}`.trim() },
                  { header: 'Year', cell: (v) => v.year ?? '–' },
                  { header: 'Plate', cell: (v) => <span className="font-mono text-xs">{v.license_plate ?? '–'}</span> },
                  {
                    header: 'Status',
                    cell: (v) => (
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${STATUS_TONE[v.service_status ?? 'in_service']}`}
                      >
                        {STATUS_LABEL[v.service_status ?? 'in_service']}
                      </span>
                    ),
                  },
                ]}
              />
            )}
          </SectionHeader>
        ))
      )}
    </div>
  );
}

function VehicleCard({ vehicle, onOpen }: { vehicle: Vehicle; onOpen: () => void }): JSX.Element {
  const status = vehicle.service_status ?? 'in_service';
  return (
    <Card
      interactive
      onClick={onOpen}
      thumbnail={<EntityImage entityType="vehicle" entityId={vehicle.id} alt={vehicle.name} />}
      title={vehicle.name}
      subtitle={[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') || vehicle.license_plate || 'Vehicle'}
      badge={
        <span className={`rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${STATUS_TONE[status]}`}>
          {STATUS_LABEL[status]}
        </span>
      }
      footer={
        <div className="flex items-center justify-between">
          <span className="text-burnt-orange dark:text-burnt-orange-dark">Open</span>
          <Trash2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </div>
      }
    />
  );
}

function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border bg-surface px-2 text-sm shadow-sm dark:bg-surface-dark dark:border-border"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function SkeletonGrid(): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} className="h-64 rounded-xl" />
      ))}
    </div>
  );
}

interface ListColumn<T> {
  header: string;
  cell: (row: T) => React.ReactNode;
}
function ListTable<T extends { id: string }>({
  rows,
  columns,
  onRow,
}: {
  rows: T[];
  columns: ListColumn<T>[];
  onRow: (row: T) => void;
}): JSX.Element {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface dark:bg-surface-dark dark:border-border">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-surface-raised text-left dark:bg-surface-raised-dark">
          <tr>
            {columns.map((c) => (
              <th key={c.header} className="px-4 py-2 font-mono text-2xs uppercase tracking-wider text-muted-foreground">
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="cursor-pointer border-b border-border last:border-0 hover:bg-surface-raised dark:hover:bg-surface-raised-dark"
              onClick={() => onRow(row)}
            >
              {columns.map((c) => (
                <td key={c.header} className="px-4 py-3">{c.cell(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
